<template>
  <div class="content-main">
    <!-- Create Location Modal -->
    <BaseModal v-model="showCreateModal" @close="closeCreateModal">
      <div class="p-6" style="min-width: 450px">
        <h3 class="text-lg font-semibold mb-4">New Location</h3>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Department</label>
          <select
            v-model="form.departamento_id"
            class="w-full border rounded px-3 py-2"
            @change="onDepartmentChange"
          >
            <option value="">Select department...</option>
            <option
              v-for="dept in departments"
              :key="dept.departamentoId"
              :value="dept.departamentoId"
            >
              {{ dept.nombre || dept.codigo }}
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">
            Deposito (Warehouse)
          </label>
          <select
            v-model="form.warehouse_id"
            class="w-full border rounded px-3 py-2"
          >
            <option value="">Select deposito...</option>
            <option
              v-for="dep in filteredDepositos"
              :key="dep.depositoId || dep.id"
              :value="dep.depositoId"
            >
              {{ dep.descDeposito }} ({{ dep.codDeposito }})
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Name *</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full border rounded px-3 py-2"
            placeholder="e.g. A-01-01"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Zone</label>
          <input
            v-model="form.zone"
            type="text"
            class="w-full border rounded px-3 py-2"
            placeholder="e.g. Zone A"
          />
        </div>
        <div class="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm font-medium mb-1">Rack</label>
            <input
              v-model="form.rack"
              type="text"
              class="w-full border rounded px-3 py-2"
              placeholder="e.g. Rack 01"
            />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Bin</label>
            <input
              v-model="form.bin"
              type="text"
              class="w-full border rounded px-3 py-2"
              placeholder="e.g. Bin 01"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100"
            @click="closeCreateModal"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            @click="saveLocation"
            :disabled="saving || !form.warehouse_id || !form.name"
          >
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <!-- Upload Modal -->
    <BaseModal v-model="showUploadModal" @close="closeUploadModal">
      <div class="p-6" style="min-width: 400px">
        <h3 class="text-lg font-semibold mb-4">Upload Locations</h3>
        <p class="text-sm text-gray-600 mb-4">
          Upload an Excel file (.xlsx) with the locations to create.
          <a
            href="#"
            class="text-blue-600 hover:underline"
            @click.prevent="handleDownloadTemplate"
          >
            Download template
          </a>
        </p>
        <div class="mb-4">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            class="w-full border rounded px-3 py-2"
            @change="onFileSelected"
          />
        </div>
        <div
          v-if="uploadResult"
          class="mb-4 p-3 rounded text-sm"
          :class="{
            'bg-green-50 text-green-800':
              uploadResult.inserted > 0 && uploadResult.errors.length === 0,
            'bg-yellow-50 text-yellow-800':
              uploadResult.inserted > 0 && uploadResult.errors.length > 0,
            'bg-red-50 text-red-800':
              uploadResult.inserted === 0 && uploadResult.errors.length > 0
          }"
        >
          <div v-if="uploadResult.inserted > 0">
            {{ uploadResult.inserted }} location(s) created successfully.
          </div>
          <div v-if="uploadResult.errors.length > 0" class="mt-1">
            <strong>Errors:</strong>
            <ul class="list-disc ml-4 mt-1">
              <li v-for="(err, i) in uploadResult.errors" :key="i">
                {{ err }}
              </li>
            </ul>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100"
            @click="closeUploadModal"
          >
            Close
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            @click="handleUpload"
            :disabled="!selectedFile || uploading"
          >
            {{ uploading ? "Uploading..." : "Upload" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <div class="content" style="width: 100%">
      <div class="header flex justify-between items-center">
        <span>Warehouse Locations</span>
        <div class="flex gap-2">
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            @click="openCreateModal"
          >
            + New Location
          </button>
          <button
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            @click="handleDownloadTemplate"
          >
            Download Template
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            @click="openUploadModal"
          >
            Upload Locations
          </button>
        </div>
      </div>

      <div class="mb-4 px-4 flex gap-4">
        <input
          v-model="search"
          type="text"
          class="flex-1 border rounded px-3 py-2"
          placeholder="Search by name, zone, rack, bin..."
          @input="debouncedLoad"
        />
        <select
          v-model="filterWarehouse"
          class="border rounded px-3 py-2"
          @change="
            page = 1;
            loadLocations();
          "
        >
          <option value="">All Warehouses</option>
          <option
            v-for="wh in warehouses"
            :key="wh.warehouseId || wh.warehouse_id"
            :value="wh.warehouseId || wh.warehouse_id"
          >
            {{
              wh.warehouseCode ||
                wh.warehouse_code ||
                wh.warehouseId ||
                wh.warehouse_id
            }}
          </option>
        </select>
      </div>

      <div class="table">
        <table class="default-table" v-if="!loading">
          <thead>
            <tr class="table100-head">
              <th>ID</th>
              <th>Department</th>
              <th>Warehouse</th>
              <th>Name</th>
              <th>Zone</th>
              <th>Rack</th>
              <th>Bin</th>
              <th>Products</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="loc in locations" :key="loc.id">
              <td>{{ loc.id }}</td>
              <td>{{ loc.departamentoId || "-" }}</td>
              <td>{{ loc.warehouseId }}</td>
              <td>{{ loc.name }}</td>
              <td>{{ loc.zone || "-" }}</td>
              <td>{{ loc.rack || "-" }}</td>
              <td>{{ loc.bin || "-" }}</td>
              <td>
                <span
                  class="px-2 py-1 text-xs rounded"
                  :class="
                    loc.productsCount > 0
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  "
                >
                  {{ loc.productsCount }}
                </span>
              </td>
              <td>
                <span
                  class="px-2 py-1 text-xs rounded"
                  :class="
                    loc.active == 1
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  "
                >
                  {{ loc.active == 1 ? "Active" : "Inactive" }}
                </span>
              </td>
              <td>
                <button
                  v-if="loc.productsCount == 0"
                  class="text-red-600 hover:underline text-sm"
                  @click="confirmDelete(loc)"
                >
                  Delete
                </button>
                <span v-else class="text-gray-400 text-sm">
                  Has products
                </span>
              </td>
            </tr>
            <tr v-if="locations.length === 0">
              <td colspan="10" class="text-center text-gray-500 py-4">
                No locations found
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="p-8 text-center">Loading...</div>
      </div>

      <div class="footer">
        <div class="totals">
          showing {{ perPage * (page - 1) + 1 }} to
          {{ Math.min(perPage * page, totalItems) }} from
          {{ totalItems }} results
        </div>
        <div class="pages">
          <select
            v-model="perPage"
            @change="
              page = 1;
              loadLocations();
            "
          >
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          Per Page
        </div>
        <div class="pages">
          <a
            class="page-link"
            :class="{ disabled: page <= 1 }"
            @click="previous"
          >
            Previous
          </a>
          <span>Page {{ page }}</span>
          <a
            class="page-link"
            :class="{ disabled: page >= totalPages }"
            @click="next"
          >
            Next
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getLocations,
  createLocation,
  deleteLocation,
  downloadTemplate,
  uploadLocations
} from "@/api/WarehouseLocationsService.js";
import { getDepartamentos, getDepositos } from "@/api/Infoauto.js";

export default {
  name: "WarehouseLocationsPage",
  data() {
    return {
      locations: [],
      warehouses: [],
      departments: [],
      depositos: [],
      loading: false,
      saving: false,
      search: "",
      filterWarehouse: "",
      page: 1,
      perPage: 20,
      totalItems: 0,
      debounceTimer: null,
      showCreateModal: false,
      showUploadModal: false,
      selectedFile: null,
      uploading: false,
      uploadResult: null,
      form: {
        departamento_id: "",
        warehouse_id: "",
        name: "",
        zone: "",
        rack: "",
        bin: ""
      }
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.perPage) || 1;
    },
    filteredDepositos() {
      if (!this.form.departamento_id) return this.depositos;
      return this.depositos.filter(
        d =>
          d.departamentoId === this.form.departamento_id ||
          d.departamento_id === this.form.departamento_id
      );
    }
  },
  mounted() {
    this.loadLocations();
    this.loadWarehouses();
    this.loadFormData();
  },
  methods: {
    async loadLocations() {
      this.loading = true;
      try {
        const res = await getLocations({
          page: this.page,
          per_page: this.perPage,
          search: this.search || undefined,
          warehouse_id: this.filterWarehouse || undefined
        });
        this.locations = res.items || [];
        this.totalItems = res.total || 0;
      } finally {
        this.loading = false;
      }
    },
    async loadWarehouses() {
      try {
        // Extract unique warehouse_ids from locations or use a dedicated endpoint
        const res = await getLocations({ per_page: 9999 });
        const items = res.items || [];
        const seen = {};
        this.warehouses = items.reduce((acc, loc) => {
          const wid = loc.warehouseId || loc.warehouse_id;
          if (wid && !seen[wid]) {
            seen[wid] = true;
            acc.push({ warehouseId: wid, warehouseCode: wid });
          }
          return acc;
        }, []);
      } catch {
        // Warehouse filter won't be populated
      }
    },
    async loadFormData() {
      try {
        const [depts, deps] = await Promise.all([
          getDepartamentos(),
          getDepositos()
        ]);
        this.departments = depts || [];
        this.depositos = deps || [];
      } catch {
        // Form selects won't be fully populated
      }
    },
    onDepartmentChange() {
      this.form.warehouse_id = "";
    },
    openCreateModal() {
      this.form = {
        departamento_id: "",
        warehouse_id: "",
        name: "",
        zone: "",
        rack: "",
        bin: ""
      };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async saveLocation() {
      this.saving = true;
      try {
        await createLocation({
          warehouse_id: this.form.warehouse_id,
          departamento_id: this.form.departamento_id || null,
          name: this.form.name,
          zone: this.form.zone || null,
          rack: this.form.rack || null,
          bin: this.form.bin || null
        });
        this.closeCreateModal();
        this.loadLocations();
        this.loadWarehouses();
      } catch (e) {
        alert(e?.response?.data?.error || "Error creating location");
      } finally {
        this.saving = false;
      }
    },
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.loadLocations();
      }, 300);
    },
    confirmDelete(loc) {
      if (
        confirm(`Delete location "${loc.name}"? This action is irreversible.`)
      ) {
        this.doDelete(loc);
      }
    },
    async doDelete(loc) {
      try {
        await deleteLocation(loc.id);
        this.loadLocations();
      } catch (e) {
        alert(e?.response?.data?.error || "Error deleting location");
      }
    },
    async handleDownloadTemplate() {
      try {
        await downloadTemplate();
      } catch {
        alert("Error downloading template");
      }
    },
    openUploadModal() {
      this.selectedFile = null;
      this.uploadResult = null;
      this.showUploadModal = true;
    },
    closeUploadModal() {
      this.showUploadModal = false;
      this.selectedFile = null;
      if (this.uploadResult && this.uploadResult.inserted > 0) {
        this.loadLocations();
      }
    },
    onFileSelected(e) {
      this.selectedFile = e.target.files[0] || null;
      this.uploadResult = null;
    },
    async handleUpload() {
      if (!this.selectedFile) return;
      this.uploading = true;
      this.uploadResult = null;
      try {
        const result = await uploadLocations(this.selectedFile);
        this.uploadResult = result;
        if (result.inserted > 0) {
          this.loadLocations();
        }
      } catch (e) {
        this.uploadResult = {
          inserted: 0,
          errors: [e?.response?.data?.error || "Error uploading file"]
        };
      } finally {
        this.uploading = false;
      }
    },
    previous() {
      if (this.page > 1) {
        this.page--;
        this.loadLocations();
      }
    },
    next() {
      if (this.page < this.totalPages) {
        this.page++;
        this.loadLocations();
      }
    }
  }
};
</script>
